/* eslint-disable no-unused-vars */
/* eslint-disable react/forbid-prop-types */
import { Button, Loader, TriPane } from '@pwc/pwc-deals-ui';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { Constants, useMobile, USER_ROLES } from '@pwc/pwc-deals-web-common';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import {
  getFakeClientUser,
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { useDispatch, useSelector } from 'react-redux';
import AutoLock from 'common/components/AutoLock';
import AddInsightButton from 'common/components/AddInsightButton/AddInsightButton.tsx';
import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import { LockHttpClient } from 'common/services/httpClient';
import CalloutBox from 'common/components/CalloutBox/CalloutBox';
import { LinkedContentNavigation } from 'common/components/LinkedContentNavigation/LinkedContentNavigation';
import ModalConfirmation from 'common/components/ModalConfirmation';
import { inlineEditorConfig } from 'common/components/RichTextEditor/CKEditorConfig';
import SourceDisplay from 'common/components/SourceDisplay/SourceDisplay';
import VersionToggleContainer from 'common/components/VersionToggle/VersionToggleContainer';
import { getStructuredAnalysisViewLoadingInProcess } from 'common/engine/reducers/structuredAnalysisViewSlice';
import {
  loadBasisOfPresenation,
  getAnalysisDescription,
} from 'common/engine/reducers/structuredDocumentDescriptionSlice';
import { STRUCTURED_DISPLAY_NAME_BY_CODE } from 'common/engine/utilities/constants';
import featureFlags from 'common/helpers/featureFlags';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import useVersionToggle from 'common/hooks/useVersionToggle';
import userTokenService from 'common/services/security/userTokenService';
import {
  entitySharedStatusUpdateSuccess,
  getEntityId,
  setEntityId,
} from 'features/comments/commentsSlice';
import ConnectedContent from 'features/narratives/ConnectedContent/NewConnectedContent';
import useConnectedContent from 'features/narratives/hooks/useConnectedContent';
import EnableForProjectState from 'features/security/EnableForProjectState';
import ShowForRole from 'features/security/ShowForRole';
import AnalysisControlsHeader from 'features/analyses/common/AnalysisControlsHeader';
import NotesDisplay from 'features/analyses/common/NotesDisplay/NotesDisplay';
import TagList from 'features/analyses/common/TagList/TagList';
import ClientManipulationBar from '../../ClientManipulationBar/ClientManipulationBar';
import AnalysisRichtextDescription from '../AnalysisRichtextDescription/AnalysisRichtextDescription';
import AutosyncedSource from '../AutosyncedSource/AutosyncedSource';
import StructuredAnalysisErrorCard from '../StructuredAnalysisErrorCard/StructuredAnalysisErrorCard';
import StructuredAnalysisFileDownload from '../StructuredAnalysisFileDownload/StructuredAnalysisFileDownload';
import StructuredAnalysisHeader from '../StructuredAnalysisHeader/StructuredAnalysisHeader';
import './StructuredAnalysisView.scss';
import styles from './StructuredAnalysisView.module.scss';
import {
  chartForDocumentCode,
  tableForDocumentCode,
} from './structuredComponentToDocumentCodeMap';
import { analysesListPath } from '../../../../../common/services/urlService';

const StructuredAnalysisView = ({
  projectId,
  documentCode,
  hasError,
  isLoading,
  isConfirmVisible,
  setIsConfirmVisible,
  handleDelete,
  chartKey,
  incrementChartKey,
  analysis,
  workflowActions,
  handleRefresh,
  handleEdit,
  versions,
  viewClient,
  isClientView,
  targetAdjustmentType,
  targetAdjustmentName,
  initialPaneOneAvailable,
  initialPaneTwoAvailable,
  initialPaneThreeAvailable,
  paneOneVisibilityDefaultValue,
  paneTwoVisibilityDefaultValue,
  paneThreeVisibilityDefaultValue,
}) => {
  const history = useHistory();
  const isMobile = useMobile();
  const isMountedRef = useIsMountedRef();
  const TableComponent = tableForDocumentCode(documentCode);
  const ChartComponent = chartForDocumentCode(documentCode);
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isFakeClient = useSelector(getFakeClientUser);
  const linkedConnections = useConnectedContent(analysis && analysis.contentId);
  const loadingInProcess = useSelector(
    getStructuredAnalysisViewLoadingInProcess
  );
  const { loading } = useSelector(getAnalysisDescription);
  const commentsEntityId = useSelector(getEntityId);
  const { versionData, onShowClientVersion, onShowDraftVersion } =
    viewClient || {};

  const { draftVersion, sharedVersion } = versionData || {};

  const enableVersionToggle = useMemo(
    () =>
      isMountedRef.current && commentsEntityId === analysis?.contentThreadId,
    [isMountedRef, commentsEntityId, analysis?.contentThreadId]
  );

  const { isEntitySharedWithClient, commentCounts } = useVersionToggle(
    enableVersionToggle,
    draftVersion,
    sharedVersion
  );

  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  const scrollToBottom = () => {
    window.scroll({
      top: document.body.offsetHeight,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleEditClick = () => {
    handleEdit();
  };

  const contextStatusInfoItem = {
    userViewStatus: analysis?.userViewStatus,
    versionStatus: analysis?.versionStatus,
    shareWithAll: analysis?.shareWithAll,
    isViewMode: true,
    isEditMode: false,
  };

  useEffect(() => {
    if (analysis?.contentThreadId && !enableVersionToggle) {
      dispatch(setEntityId(analysis.contentThreadId));
    }
  }, [analysis?.contentThreadId, dispatch, enableVersionToggle]);

  useEffect(() => {
    if (analysis && !isLoading) {
      dispatch(entitySharedStatusUpdateSuccess(analysis.shareWithAll));
      dispatch(
        loadBasisOfPresenation(
          projectId,
          documentCode,
          analysis.contentThreadId,
          analysis.versionNumber,
          analysis.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
            analysis.versionStatus ===
              Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
        )
      );
    }
  }, [projectId, documentCode, analysis, dispatch, isLoading]);

  useEffect(() => {
    if (
      analysis &&
      analysis?.contentThreadId &&
      !loadingInProcess &&
      !isLoading &&
      !hasError &&
      isFakeClient &&
      (!sharedVersion || isEmpty(sharedVersion))
    ) {
      history.push(analysesListPath(projectId));
    }
  }, [
    analysis,
    analysis?.contentThreadId,
    isFakeClient,
    sharedVersion,
    projectId,
    history,
    hasError,
    isLoading,
    loadingInProcess,
  ]);

  if (hasError) {
    return <StructuredAnalysisErrorCard />;
  }

  if (isLoading || loadingInProcess) {
    return <Loader dataTestId="structured-loading-indicator" />;
  }

  const handleSharedClick = () => {
    if (onShowClientVersion && !loading) {
      onShowClientVersion();
    }
  };

  const handleDraftClick = () => {
    if (onShowDraftVersion && !loading) {
      onShowDraftVersion();
    }
  };

  const analysisTitle = featureFlags?.isStructuredAnalysesTitleEditable
    ? analysis?.title
    : STRUCTURED_DISPLAY_NAME_BY_CODE[documentCode];

  return analysis && analysis.contentThreadId ? (
    <SyncedInfoProvider value={contextStatusInfoItem}>
      <div
        className={classnames(
          'structured-analysis-view-wrapper view-sa-scroll-component',
          !isPwcUser ? 'current-user-is-client' : 'current-user-is-pwc'
        )}
      >
        <ModalConfirmation
          show={isConfirmVisible}
          onCancel={() => setIsConfirmVisible(false)}
          onConfirm={handleDelete}
          confirmTitle={`Confirm ${analysisTitle} Delete`}
          confirmationMessage="This Analysis will be permanently deleted and any connections to other content will be removed."
          confirmButtonText="Yes, delete"
        />

        <TriPane
          onChange={incrementChartKey}
          initialPaneOneAvailable={initialPaneOneAvailable}
          initialPaneTwoAvailable={initialPaneTwoAvailable}
          initialPaneThreeAvailable={initialPaneThreeAvailable}
          paneOneVisibilityDefaultValue={paneOneVisibilityDefaultValue}
          paneTwoVisibilityDefaultValue={paneTwoVisibilityDefaultValue}
          paneThreeVisibilityDefaultValue={paneThreeVisibilityDefaultValue}
        >
          <AutoLock
            projectId={projectId}
            contentThreadIds={[analysis.contentThreadId]}
          >
            <AnalysisControlsHeader
              commentingAutoLockContentId={analysis.contentThreadId}
              workflowActions={workflowActions}
              onRefresh={handleRefresh}
              onEdit={handleEditClick}
              onDelete={() => setIsConfirmVisible(true)}
              versions={versions}
              analysis={analysis}
              viewClient={viewClient}
              isInternalUser={isPwcUser}
              disableEdit={isMobile}
            >
              <VersionToggleContainer
                mode="view"
                isEntitySharedWithClient={isEntitySharedWithClient}
                projectId={projectId}
                config={{
                  commentCounts,
                  draftVersion,
                  sharedVersion,
                  draftOnClick: handleDraftClick,
                  sharedOnClick: handleSharedClick,
                }}
                lockedThreadId={analysis.contentThreadId}
              />
            </AnalysisControlsHeader>
          </AutoLock>

          <div className="structured-analysis-view">
            <div className="structured-analysis-view__header">
              <div
                className="structured-analysis-view__analysis__contents"
                data-testid="structured-analysis-view"
              >
                <div
                  className={styles.header}
                  data-testid="structured-analysis-header"
                >
                  <p className="top-of-type">Analysis</p>
                  <StructuredAnalysisHeader
                    title={analysisTitle}
                    projectId={projectId}
                    analysis={analysis}
                    isOnClientVersion={viewClient.isOnClientVersion}
                    shareWithAll={isEntitySharedWithClient}
                  />
                  {!isPwcUser && (
                    <div className="check-linked-connections">
                      <Button
                        data-testid="popover-confirmation-cancel"
                        onClick={scrollToBottom}
                        className="button-link mr-3"
                      >
                        Check connections
                      </Button>
                    </div>
                  )}

                  {analysis.insight && (
                    <div className={styles.calloutBox}>
                      <CalloutBox
                        iconName="light-bulb"
                        iconClass="icon fill icon-grey-theme"
                        headerLabel="Insight"
                        text={analysis.insight}
                        dataTestId="insight"
                        usage="detail"
                      />
                    </div>
                  )}
                  <EnableForProjectState projectId={projectId}>
                    {({ isProjectDisabled }) =>
                      !analysis.insight &&
                      !isProjectDisabled && (
                        <AutoLock
                          projectId={projectId}
                          contentThreadIds={[analysis.contentThreadId]}
                        >
                          <AddInsightButton
                            projectId={projectId}
                            contentThreadId={analysis.contentThreadId}
                            onClick={handleEdit}
                          />
                        </AutoLock>
                      )
                    }
                  </EnableForProjectState>
                </div>
                <div className="structured-analysis-view__linked-navigaton-container">
                  {analysis.contentId && (
                    <LinkedContentNavigation
                      projectId={projectId}
                      connectedAnalyses={linkedConnections.connectedAnalyses}
                      connectedKfas={linkedConnections.connectedKfas}
                      hypothesisIndex={1}
                      currentContent={analysis}
                      parentContentId={analysis.contentId}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="tri-pane-container" data-testid="tri-pane-display">
              <TriPane.HeaderContent>
                <TriPane.Controls />
              </TriPane.HeaderContent>
              <TriPane.PaneOne>
                <>
                  <div className="scrollable structured-table">
                    <TableComponent
                      isClientView={
                        isClientView ||
                        analysis.versionStatus ===
                          Constants.VERSION_STATUS.SHARED.value
                      }
                      contentId={analysis.contentThreadId}
                      contentVersionNumber={analysis.versionNumber}
                      projectId={projectId}
                      targetAdjustmentType={targetAdjustmentType}
                      targetAdjustmentName={targetAdjustmentName}
                    />
                  </div>
                  <div className="autosynced-source-link">
                    <AutosyncedSource
                      projectId={projectId}
                      contentSubType={analysis?.contentSubType}
                    />
                    {analysis && featureFlags.downloadExcelTableEnabled && (
                      <StructuredAnalysisFileDownload
                        title={analysis.title}
                        contentThreadId={analysis.contentThreadId}
                        isShared={
                          analysis.versionStatus ===
                          Constants.VERSION_STATUS.SHARED.value
                        }
                        versionNumber={analysis.versionNumber}
                        isThirdPartyMember={isThirdPartyMember}
                      />
                    )}
                  </div>
                </>
              </TriPane.PaneOne>
              <TriPane.PaneTwo>
                <ChartComponent key={chartKey} />
              </TriPane.PaneTwo>
              <TriPane.PaneThree>
                <>
                  <AnalysisRichtextDescription
                    projectId={projectId}
                    isReadonly
                    lightModeVariant
                    config={inlineEditorConfig}
                    documentCode={documentCode}
                  />
                </>
              </TriPane.PaneThree>
              <TriPane.SourceContent>
                <SourceDisplay
                  sourceText={analysis.source}
                  icon="docs"
                  showSeparator
                />
                <div className="notes-display">
                  <NotesDisplay text={analysis.notes} />
                </div>
                <TagList tags={analysis.tags} testId="analysis-tag-list" />
              </TriPane.SourceContent>
            </div>
          </div>
        </TriPane>

        <ConnectedContent
          classModifier="structured-analysis"
          projectId={projectId}
          contentId={analysis.contentId}
          linkedConnections={linkedConnections}
          viewAsClient={
            isClientView ||
            analysis.versionStatus === Constants.VERSION_STATUS.SHARED.value
          }
        />
        {analysis.contentId && !isPwcUser && (
          <div className="bottom-linked-navigation-client">
            <LinkedContentNavigation
              projectId={projectId}
              connectedAnalyses={linkedConnections.connectedAnalyses}
              connectedKfas={linkedConnections.connectedKfas}
              hypothesisIndex={1}
              currentContent={analysis}
              parentContentId={analysis.contentId}
            />
          </div>
        )}
        <ClientManipulationBar />
      </div>
    </SyncedInfoProvider>
  ) : null;
};

StructuredAnalysisView.propTypes = {
  projectId: PropTypes.string.isRequired,
  documentCode: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isConfirmVisible: PropTypes.bool.isRequired,
  setIsConfirmVisible: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  chartKey: PropTypes.number.isRequired,
  incrementChartKey: PropTypes.func,
  analysis: PropTypes.object,
  workflowActions: PropTypes.array.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  versions: PropTypes.array,
  viewClient: PropTypes.object.isRequired,
  isClientView: PropTypes.bool,
  targetAdjustmentType: PropTypes.string,
  targetAdjustmentName: PropTypes.string,
  initialPaneOneAvailable: PropTypes.bool,
  initialPaneTwoAvailable: PropTypes.bool,
  initialPaneThreeAvailable: PropTypes.bool,
  paneOneVisibilityDefaultValue: PropTypes.bool,
  paneTwoVisibilityDefaultValue: PropTypes.bool,
  paneThreeVisibilityDefaultValue: PropTypes.bool,
};

StructuredAnalysisView.defaultProps = {
  hasError: false,
  isLoading: false,
  analysis: undefined,
  isClientView: false,
  targetAdjustmentType: null,
  targetAdjustmentName: null,
  initialPaneOneAvailable: true,
  initialPaneTwoAvailable: true,
  initialPaneThreeAvailable: true,
  paneOneVisibilityDefaultValue: true,
  paneTwoVisibilityDefaultValue: true,
  paneThreeVisibilityDefaultValue: true,
  versions: null,
  incrementChartKey: () => {},
};

export default StructuredAnalysisView;
